import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import { Campaign } from '@local-types';
import {
  BulkUpdateResponse,
  BulkValidateResponse,
  useBulkValidate,
  useBulkUpdate,
  formatDateTimeForApi,
  BulkValidateRequestBody,
  BulkUpdateRequestBody,
  useBulkValidateCreatives,
  useBulkUpdateCreatives
} from '@components/hooks/apis/bulk';
import { MockAdGroup } from '@components/tests/__mocks__';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { BidStrategyEvent } from '@local-types/bidStrategy';
import { AdvertiserContextType } from '@components/AdvertiserContext';
import {
  BulkEditStep,
  bulkEditSteps,
  BulkEditFieldState,
  BulkEditFieldValueState,
  FormattedDates,
  EditAdGroupDatesTimesFormValues,
  BulkUpdateArgs,
  ReviewEditsRow,
  getHeader,
  EditCreativesFormValues
} from '../constants';
import { compileTableRows, getInvalidCount } from '../components/ReviewEditsPage/utils';
import { transformBidStrategies } from '../constants/bidStrategyOptions';
import { BidStrategyFormValues, BidStrategyOption } from '../types';

export interface BulkEditDatesTimesFormData {
  startDate: moment.Moment | null;
  startTime: string | null;
  endDate: moment.Moment | null;
  endTime: string | null;
}

interface BulkEditContextState {
  // Original state
  datesTimesFormData: BulkEditDatesTimesFormData;
  validationResult: BulkValidateResponse | null;
  isValidating: boolean;
  validationError: Error | null;
  updateResult: BulkUpdateResponse | null;
  isUpdating: boolean;
  updateError: Error | null;
  setDatesTimesFormData: (data: BulkEditDatesTimesFormData) => void;
  setValidationResult: (result: BulkValidateResponse | null) => void;
  setValidationState: (isValidating: boolean, error: Error | null) => void;
  setUpdateResult: (result: BulkUpdateResponse | null) => void;
  setUpdateState: (isUpdating: boolean, error: Error | null) => void;
  resetContext: () => void;

  // Added state
  step: BulkEditStep;
  field: BulkEditFieldState;
  fieldValues: BulkEditFieldValueState;
  isNextEnabled: boolean;
  ids: number[];
  invalidCount: number;
  rowSelectionModel: GridRowSelectionModel;
  tableRows: ReviewEditsRow[];
  adGroups: MockAdGroup[];
  campaign?: Campaign;
  bidStrategyOptions: BidStrategyOption[];
  bidStrategyEventSet: BidStrategyEvent[];
  // Added methods
  setStep: (step: BulkEditStep) => void;
  setField: (field: BulkEditFieldState) => void;
  setFieldValues: (values: BulkEditFieldValueState) => void;

  handleBackButton: () => void;
  handleSelectField: (value: string) => void;
  handleDatesTimesSubmit: (formData: EditAdGroupDatesTimesFormValues) => void;
  handleBidStrategySubmit: (values: BidStrategyFormValues) => void;
  handleCreativesSubmit: (values: EditCreativesFormValues) => void;
  handleApplyEdits: (args: BulkUpdateArgs) => Promise<void>;
  handleSelectionChange: (newSelection: GridRowSelectionModel) => void;
  handleModalClose: () => void;

  validateBulk: (args: BulkValidateRequestBody) => Promise<BulkValidateResponse>;
  updateBulk: (args: BulkUpdateRequestBody) => Promise<BulkUpdateResponse>;

  getHeaderText: () => string;
}

const initialState: BulkEditContextState = {
  // Original state
  datesTimesFormData: {
    startDate: null,
    startTime: null,
    endDate: null,
    endTime: null,
  },
  validationResult: null,
  isValidating: false,
  validationError: null,
  updateResult: null,
  isUpdating: false,
  updateError: null,
  setDatesTimesFormData: () => {},
  setValidationResult: () => {},
  setValidationState: () => {},
  setUpdateResult: () => {},
  setUpdateState: () => {},
  resetContext: () => {},

  // Added state
  step: bulkEditSteps.SELECT_FIELD,
  field: 'Unselected',
  fieldValues: 'None',
  isNextEnabled: false,
  ids: [],
  invalidCount: 0,
  rowSelectionModel: [],
  tableRows: [],
  adGroups: [],
  campaign: undefined,

  // Bid Strategy related
  bidStrategyOptions: [],
  bidStrategyEventSet: [],
  // Added methods
  setStep: () => {},
  setField: () => {},
  setFieldValues: () => {},

  handleBackButton: () => {},
  handleSelectField: () => {},
  handleDatesTimesSubmit: () => Promise.resolve(),
  handleBidStrategySubmit: () => {},
  handleApplyEdits: () => Promise.resolve(),
  handleSelectionChange: () => {},
  handleModalClose: () => {},

  validateBulk: () => Promise.resolve({ count: 0, results: [] } as BulkValidateResponse),
  updateBulk: () => Promise.resolve({} as BulkUpdateResponse),

  getHeaderText: () => '',
};

export const BulkEditContext =
  createContext<BulkEditContextState>(initialState);

interface BulkEditProviderProps {
  children: ReactNode;
  adGroups: MockAdGroup[];
  campaign?: Campaign;
  advertiser?: AdvertiserContextType;
  onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown') => void;
}

export const BulkEditProvider = ({
  children,
  adGroups,
  campaign,
  advertiser,
  onClose
}: BulkEditProviderProps) => {
  // Original state
  const [datesTimesFormData, setDatesTimesFormData] =
    useState<BulkEditDatesTimesFormData>(initialState.datesTimesFormData);

  const [validationResult, setValidationResult] =
    useState<BulkValidateResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<Error | null>(null);

  const [updateResult, setUpdateResult] = useState<BulkUpdateResponse | null>(
    null
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // Added state
  const [step, setStep] = useState<BulkEditStep>(bulkEditSteps.SELECT_FIELD);
  const [field, setField] = useState<BulkEditFieldState>('Unselected');
  const [fieldValues, setFieldValues] = useState<BulkEditFieldValueState>('None');
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [ids, setIds] = useState<number[]>([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const [tableRows, setTableRows] = useState<ReviewEditsRow[]>([]);

  const shouldShowBidStrategy = useMemo(() => {
    return !!(
      (advertiser?.bidstrategy_set ?? []).length > 0 &&
      advertiser?.tvsciq &&
      advertiser?.tvsciq !== 'DISABLED'
    );
  }, [advertiser]);

  const bidStrategyEventSet = useMemo(() => {
    return advertiser?.bidstrategyevent_set || [];
  }, [advertiser]);

  const bidStrategyOptions = useMemo(() => {
    if (!shouldShowBidStrategy) return [];

    return transformBidStrategies(
      advertiser?.bidstrategy_set,
      bidStrategyEventSet,
    );
  }, [advertiser, shouldShowBidStrategy, bidStrategyEventSet]);

  // Hooks for API operations
  const { trigger: validateBulk } = useBulkValidate({
    onSuccess: data => {
      setValidationResult(data);
      setValidationState(false, null);
    },
    onError: error => {
      setValidationState(false, error);
    },
  });

  const { trigger: updateBulk } = useBulkUpdate({
    onSuccess: data => {
      setUpdateResult(data);
      setUpdateState(false, null);
    },
    onError: error => {
      console.error('Bulk update error:', error);
      setUpdateState(
        false,
        error instanceof Error ? error : new Error(String(error))
      );
    },
  });

  // Creatives api endpoints
  const { trigger: validateBulkCreatives } = useBulkValidateCreatives({
    onSuccess: data => {
      setValidationResult(data);
      setValidationState(false, null);
    },
    onError: error => {
      setValidationState(false, error);
    },
  });

  const { trigger: updateBulkCreatives } = useBulkUpdateCreatives({
    onSuccess: data => {
      setUpdateResult(data);
      setUpdateState(false, null);
    },
    onError: error => {
      console.error('Bulk update error:', error);
      setUpdateState(
        false,
        error instanceof Error ? error : new Error(String(error))
      );
    },
  });

  // Original methods
  const setValidationState = useCallback((isValidating: boolean, error: Error | null) => {
    setIsValidating(isValidating);
    setValidationError(error);
  }, []);

  const setUpdateState = useCallback((isUpdating: boolean, error: Error | null) => {
    setIsUpdating(isUpdating);
    setUpdateError(error);
  }, []);

  const resetContext = useCallback(() => {
    setDatesTimesFormData(initialState.datesTimesFormData);
    setValidationResult(null);
    setIsValidating(false);
    setValidationError(null);
    setUpdateResult(null);
    setIsUpdating(false);
    setUpdateError(null);

    // Reset added state
    setStep(bulkEditSteps.SELECT_FIELD);
    setField('Unselected');
    setFieldValues('None');
    setIsNextEnabled(false);
    setIds([]);
    setInvalidCount(0);
    setRowSelectionModel([]);
    setTableRows([]);
  }, []);

  // Added methods
  const handleBackButton = useCallback(() => {
    if (step === bulkEditSteps.EDIT_FIELD) {
      setStep(bulkEditSteps.SELECT_FIELD);
      setField('Unselected');
    } else if (step === bulkEditSteps.REVIEW_EDITS) {
      setStep(bulkEditSteps.EDIT_FIELD);
    }
  }, [step]);

  const handleSelectField = useCallback((value: string) => {
    setField(value as BulkEditFieldState);
    setStep(bulkEditSteps.EDIT_FIELD);
  }, []);

  const handleSelectionChange = useCallback((newSelection: GridRowSelectionModel) => {
    setIds(newSelection as number[]);
    setRowSelectionModel(newSelection);

    if (!tableRows || tableRows.length === 0) {
      setInvalidCount(0);
      setIsNextEnabled(false);
      return;
    }

    const invalidCount = getInvalidCount(tableRows, newSelection);
    setInvalidCount(invalidCount);
    setIsNextEnabled(invalidCount === 0 && newSelection.length > 0);
  }, [tableRows]);

  const handleDatesTimesSubmit = useCallback((formData: EditAdGroupDatesTimesFormValues) => {
    if (!adGroups || adGroups.length === 0) {
      return;
    }

    const alreadyStarted = adGroups.some(adGroup => adGroup.active === true);

    const formattedStartDateTime = formatDateTimeForApi(formData.startDateTime);
    const formattedEndDateTime = formatDateTimeForApi(formData.endDateTime);

    const formattedDates: FormattedDates = {
      end_date: formattedEndDateTime,
    };

    if (!alreadyStarted) {
      formattedDates.start_date = formattedStartDateTime;
    }

    setValidationState(true, null);

    validateBulk({
      type: 'LINEITEM',
      select: {
        id__in: adGroups.map(group => group.id),
      },
      fields: formattedDates,
    })
      .then(_ => {
        setFieldValues(formattedDates);
        setStep(bulkEditSteps.REVIEW_EDITS);
      })
      .catch(error => {
        console.error('Validation error:', error);
        setValidationState(
          false,
          error instanceof Error ? error : new Error(String(error))
        );
      });
  }, [adGroups, validateBulk, setValidationState]);

  const handleBidStrategySubmit = useCallback((values: BidStrategyFormValues) => {
    const shouldSendBidStrategyEvent = bidStrategyOptions
      .filter(
        option =>
          option.label === 'Max Outcomes' ||
          option.label === 'Target Cost per Outcome',
      )
      .map(option => option.value)
      .includes(values.bidStrategy);

    const shouldSendBidStrategyTarget = bidStrategyOptions
      .filter(option => option.label === 'Target Cost per Outcome')
      .map(option => option.value)
      .includes(values.bidStrategy);

    const shouldSendCPMBid = bidStrategyOptions
      .filter(option => option.label === 'Manual Bid')
      .map(option => option.value)
      .includes(values.bidStrategy);

    const formattedData = {
      bid_strategy: values.bidStrategy,
      cpm: shouldSendCPMBid ? `${values.maxCPMBid}` : null,
      bid_strategy_target: shouldSendBidStrategyTarget
        ? values.bidStrategyTarget
        : null,
      bid_strategy_event: shouldSendBidStrategyEvent
        ? values.bidStrategyEvent
        : null,
    };

    setValidationState(true, null);

    validateBulk({
      type: 'LINEITEM',
      select: {
        id__in: adGroups.map(group => group.id),
      },
      fields: formattedData,
    })
      .then(_ => {
        setFieldValues(formattedData);
        setStep(bulkEditSteps.REVIEW_EDITS);
      })
      .catch(error => {
        console.error('Validation error:', error);
        setValidationState(
          false,
          error instanceof Error ? error : new Error(String(error))
        );
      });
  }, [bidStrategyOptions, adGroups, validateBulk, setValidationState]);

  const handleCreativesSubmit = useCallback(
    (values: EditCreativesFormValues) => {
      setValidationState(true, null);

      validateBulkCreatives({
        operation: 'OVERWRITE',
        select: {
          id__in: adGroups.map(group => group.id),
        },
        ...values,
      })
        .then(_ => {
          setFieldValues(values);
          setStep(bulkEditSteps.REVIEW_EDITS);
        })
        .catch(error => {
          setValidationState(
            false,
            error instanceof Error ? error : new Error(String(error)),
          );
        });
    },
    [validateBulkCreatives, setValidationState],
  );

  const handleApplyEdits = useCallback(async (args: BulkUpdateArgs) => {
    setUpdateState(true, null);

    try {
      if(args.fields.creatives) {
        await updateBulkCreatives({
          operation: 'OVERWRITE',
          select: args.select,
          creatives: args.fields.creatives,
        });
      } else {
        await updateBulk(args);
      }
    } catch (error) {
      console.error('Update error:', error);
      setUpdateState(
        false,
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }, [updateBulk, setUpdateState]);

  const [updatedIds, updatedValues] = useMemo(() => {
    const successes = updateResult?.results.filter(
      result => result.status === 'SUCCESS'
    );
    const ids = successes?.map(result => result?.body?.id) || [];
    const values =
      successes?.map(result => result?.body?.updated_fields) || [];

    return [ids, values];
  }, [updateResult]);

  // Function to refresh ad groups data when modal is closed
  const handleModalClose = useCallback(() => {
    // Pass the close function from props to a callback that first refreshes data
    if (typeof onClose === 'function') {
      // Call onClose with any successfully updated ids and values
      onClose(
        {
          updatedIds,
          updatedValues,
        },
        'backdropClick'
      );
    }
  }, [onClose, updatedIds, updatedValues]);

  const getHeaderText = useCallback(() => {
    return getHeader(step, field, adGroups.length);
  }, [step, field, adGroups.length]);

  // Update tableRows when dependencies change
  useEffect(() => {
    if (!adGroups?.length || !validationResult) {
      setTableRows([]);
      return;
    }

    const rows = compileTableRows(
      adGroups,
      validationResult,
      fieldValues !== 'None' ? fieldValues : {}
    );

    setTableRows(rows);
  }, [adGroups, validationResult, fieldValues]);

  // Update row selection when step changes to review
  useEffect(() => {
    if (step === bulkEditSteps.REVIEW_EDITS && tableRows.length > 0) {
      const allRowsSelected = tableRows.map(row => row.id);
      setRowSelectionModel(allRowsSelected);
      handleSelectionChange(allRowsSelected);
    }
  }, [step, tableRows, handleSelectionChange]);

  const value = {
    // Original state
    datesTimesFormData,
    validationResult,
    isValidating,
    validationError,
    updateResult,
    isUpdating,
    updateError,
    setDatesTimesFormData,
    setValidationResult,
    setValidationState,
    setUpdateResult,
    setUpdateState,
    resetContext,

    // Added state
    step,
    field,
    fieldValues,
    isNextEnabled,
    ids,
    invalidCount,
    rowSelectionModel,
    tableRows,
    adGroups,
    campaign,
    bidStrategyOptions,
    bidStrategyEventSet,

    // Added methods
    setStep,
    setField,
    setFieldValues,

    handleBackButton,
    handleSelectField,
    handleDatesTimesSubmit,
    handleBidStrategySubmit,
    handleCreativesSubmit,
    handleApplyEdits,
    handleSelectionChange,
    handleModalClose,

    validateBulk,
    updateBulk,

    getHeaderText,
  };

  return (
    <BulkEditContext.Provider value={value}>
      {children}
    </BulkEditContext.Provider>
  );
};

export const useBulkEdit = () => {
  const context = useContext(BulkEditContext);

  if (context === undefined) {
    throw new Error('useBulkEdit must be used within a BulkEditProvider');
  }

  return context;
};
